import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { StoreProvider, useStore } from "./context/StoreContext";
import { api } from "./lib/api";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import Chatbot from "./components/Chatbot";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductPage from "./pages/ProductPage";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import TrackOrder from "./pages/TrackOrder";
import Auth from "./pages/Auth";
import Account from "./pages/Account";
import FAQ from "./pages/FAQ";
import Legal from "./pages/Legal";
import Contact from "./pages/Contact";
import Corporate from "./pages/Corporate";
import Admin from "./pages/Admin";
import "@/App.css";

function useLenis() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function BrandLoader() {
  const [show, setShow] = useState(() => !sessionStorage.getItem("savyron_loaded"));
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => {
      sessionStorage.setItem("savyron_loaded", "1");
      setShow(false);
    }, 1300);
    return () => clearTimeout(t);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.div exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] bg-[#FFFFFF] flex flex-col items-center justify-center gap-6" data-testid="brand-loader">
          <motion.img src="/assets/savyron-logo.png" alt="SAVYRON"
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="h-20 sm:h-28 w-auto" />
          <div className="w-40 h-px bg-[#F3F4F6] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#5C1230] via-[#C9A24B] to-[#5C1230] animate-loader-line" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AuthCallback() {
  const { setUser } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        localStorage.setItem("savyron_token", data.token);
        setUser(data.user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/account", { replace: true, state: { user: data.user } });
      } catch {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/login", { replace: true });
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5" data-testid="auth-callback">
      <p className="font-serif-lux text-2xl tracking-[0.3em] gold-text font-semibold">SAVYRON</p>
      <p className="text-sm text-[#857078]">Signing you in…</p>
    </div>
  );
}

function Shell() {
  useLenis();
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div key={location.pathname}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
            <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/gifts" element={<Shop mode="all" />} />
              <Route path="/gifts/:category" element={<Shop mode="category" />} />
              <Route path="/occasions" element={<Shop mode="occasions" />} />
              <Route path="/festivals" element={<Shop mode="festivals" />} />
              <Route path="/occasion/:slug" element={<Shop mode="occasion" />} />
              <Route path="/festival/:slug" element={<Shop mode="festival" />} />
              <Route path="/recipient/:slug" element={<Shop mode="recipient" />} />
              <Route path="/search" element={<Shop mode="search" />} />
              <Route path="/offers" element={<Shop mode="offers" />} />
              <Route path="/product/:slug" element={<ProductPage />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order/:orderId" element={<OrderConfirmation />} />
              <Route path="/track" element={<TrackOrder />} />
              <Route path="/login" element={<Auth mode="login" />} />
              <Route path="/register" element={<Auth mode="register" />} />
              <Route path="/forgot" element={<Auth mode="forgot" />} />
              <Route path="/terms" element={<Legal kind="terms" />} />
              <Route path="/privacy" element={<Legal kind="privacy" />} />
              <Route path="/account" element={<Account />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/corporate" element={<Corporate />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<Shop mode="all" />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer />
      <CartDrawer />
      <Chatbot />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <StoreProvider>
          <BrandLoader />
          <Shell />
          <Toaster theme="dark" position="bottom-left" toastOptions={{
            style: { background: "#FFFFFF", border: "1px solid rgba(200,164,93,0.35)", color: "#2E1A22" },
          }} />
        </StoreProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
